import { createContext, FC, useContext, useMemo } from 'react'
import { useQuery } from 'react-query'
import { Customer, TimeZone, TimeZones } from '../../types'
import { logFactory } from '../../util/logFactory'
import { useCustomersService } from '../services'
import { useAppointmentLocationState } from '../useAppointmentLocationState'
import { useSearchParameters } from '../useSearchParameters'
import { useTimeZones } from '../useTimeZones'

interface CustomerContext {
  customer?: Customer
  isError: boolean
  isLoading: boolean
}

const isGid = (value: string | null = '') => /^[0-9]+[.][a-z-]+$/giu.test(value ?? '')

const isSalesforceId = (value: string | null = '') =>
  Boolean(value && !isGid(value) && [15, 18].indexOf(value.length) > -1) && /[0-9a-z]+/giu.test(value ?? '')

const normalizeTimeZone = ({ timeZone, timeZones }: { timeZone?: string; timeZones: TimeZones }) => {
  if (!timeZone) {
    return undefined
  }

  let tzInfo = timeZones[timeZone]

  if (!tzInfo) {
    tzInfo = Object.values(timeZones).find(({ group }) => group.includes(timeZone)) as TimeZone
  }

  return tzInfo ? tzInfo.name : undefined
}

const context = createContext<CustomerContext>({
  customer: undefined,
  isError: false,
  isLoading: true,
})

const hookName = 'useCustomer'
const log = logFactory(hookName)

const { Provider } = context

const CustomerProvider: FC = ({ children }) => {
  const { customer_gid: searchCustomerGid } = useSearchParameters()
  const { customer_gid: appointmentStateCustomerGid } = useAppointmentLocationState()
  const { getCustomers, isReady } = useCustomersService()
  const { timeZones, isLoading: areTimeZonesLoading } = useTimeZones()

  const customerGid = (searchCustomerGid as string | undefined) ?? appointmentStateCustomerGid

  const customerSearchParams = useMemo(() => {
    if (isSalesforceId(customerGid)) {
      return { salesforceId: [customerGid] }
    }

    if (isGid(customerGid)) {
      return { gid: [customerGid] }
    }

    return {}
  }, [appointmentStateCustomerGid, searchCustomerGid])

  const fetchCustomers = async (): Promise<Customer> => {
    log(`🛍️ Fetching customer...`, { customerGid })

    const { data: customers = [] } = await getCustomers(customerSearchParams)
    const [found] = customers

    log(`🛍️ Found customer`, found)

    return found
  }

  const {
    data: rawCustomer,
    isError,
    isLoading,
  } = useQuery([hookName, customerGid], fetchCustomers, {
    enabled: Boolean(Object.keys(customerSearchParams).length && isReady),
  })

  let customer: Customer | undefined

  switch (true) {
    case Boolean(rawCustomer && timeZones):
      customer = {
        ...rawCustomer,
        timezone: normalizeTimeZone({
          timeZone: rawCustomer?.timezone,
          timeZones: timeZones as TimeZones,
        }),
      } as Customer
      break
    case Boolean(rawCustomer):
      customer = {
        ...rawCustomer,
        timezone: undefined,
      } as Customer
      break
    default:
      customer = undefined
  }

  return (
    <Provider
      value={{
        customer,
        isError,
        isLoading: (!isReady || isLoading || areTimeZonesLoading) && !customer,
      }}
    >
      {children}
    </Provider>
  )
}

const useCustomer = (): CustomerContext => useContext(context)

export { CustomerProvider, useCustomer }
